import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId } from 'react';
import { Icon } from './icons.js';
const classes = (...names) => names.filter(Boolean).join(' ');
/** `pending_review` → `Pending review`: a stored value, in words for a screen. */
export const sentenceCase = (value) => value.charAt(0).toUpperCase() + value.slice(1).replaceAll('_', ' ');
/** One primary button to a card at most (DESIGN.md); labels are a verb and an object: "Merge records". */
export function Button({ variant = 'primary', type = 'button', busy = false, busyLabel, disabled, className, children, ...rest }) {
    return _jsx("button", { type: type, className: classes('button', variant !== 'primary' && variant, className), disabled: disabled || busy, ...rest, children: busy && busyLabel ? busyLabel : children });
}
/** A form's or a card's buttons, side by side, wrapping on a phone. */
export function Actions({ children, className }) {
    return _jsx("div", { className: classes('actions', className), children: children });
}
/** A plain card by default; `muted` is context needing no action, `attention` is something a person must decide. */
export function Card({ title, action, tone, children, className }) {
    return (_jsxs("section", { className: classes('card', tone === 'muted' ? 'muted-card' : tone, className), children: [(title || action) && _jsxs("div", { className: "card-title", children: [title && _jsx("h2", { children: title }), action] }), children] }));
}
/** The two-column record layout: facts on the left, decisions and restricted sections on the right; one column on a phone. */
export function Columns({ children }) {
    return _jsx("div", { className: "columns", children: children });
}
export function Column({ children }) {
    return _jsx("div", { className: "column", children: children });
}
/** The title a screen is about, exactly one to a screen, with a meta line and its actions. */
export function PageHeader({ title, meta, actions }) {
    return (_jsxs("header", { className: "page-head", children: [_jsxs("div", { children: [_jsx("h1", { children: title }), meta && _jsx("div", { className: "page-meta", children: meta })] }), actions && _jsx("div", { className: "page-actions", children: actions })] }));
}
/** `<BackLink as={Link} to="/people">People</BackLink>`: `as` is the router's link, or a plain `a` by default. */
export function BackLink({ as, children, ...rest }) {
    const Tag = as ?? 'a';
    return _jsxs(Tag, { className: "back-link", ...rest, children: [_jsx(Icon, { name: "back" }), children] });
}
/** Pairs any control with its label, hint and error, so nobody has to remember to wire them. */
export function Field({ label, hint, error, children }) {
    const id = useId();
    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;
    const describedBy = classes(error ? errorId : null, hint ? hintId : null);
    return (_jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: id, children: label }), children({ id, ...(describedBy ? { 'aria-describedby': describedBy } : {}), ...(error ? { 'aria-invalid': true } : {}) }), hint && _jsx("div", { className: "field-hint", id: hintId, children: hint }), error && _jsx("div", { className: "field-error", id: errorId, children: error })] }));
}
/** A labelled input of any type: text, email, password, file. */
export function TextField({ label, hint, error, type = 'text', ...input }) {
    return _jsx(Field, { label: label, hint: hint, error: error, children: (control) => _jsx("input", { type: type, ...input, ...control }) });
}
/** A tick box or a radio with its words beside it; pressing the words presses the box. */
export function CheckField({ label, type = 'checkbox', ...input }) {
    return _jsxs("label", { className: "check", children: [_jsx("input", { type: type, ...input }), _jsx("span", { children: label })] });
}
/** Radios or tick boxes that answer one question. */
export function FieldGroup({ legend, children }) {
    return _jsxs("fieldset", { className: "field-group", children: [_jsx("legend", { children: legend }), children] });
}
/** The pill-shaped search box, with its icon and a label for screen readers. */
export function SearchBox({ label, ...input }) {
    const id = useId();
    return (_jsxs("div", { className: "search-box", children: [_jsx(Icon, { name: "search" }), _jsx("label", { className: "visually-hidden", htmlFor: id, children: label }), _jsx("input", { id: id, type: "search", ...input })] }));
}
/** A message on the page. An error is announced to a screen reader the moment it appears. */
export function Notice({ tone = 'neutral', children }) {
    return _jsx("div", { className: classes('notice', tone !== 'neutral' && tone), role: tone === 'error' ? 'alert' : 'status', children: children });
}
export function Pill({ tone = 'neutral', children }) {
    return _jsx("span", { className: classes('pill', tone !== 'neutral' && tone), children: children });
}
export function Pills({ children }) {
    return _jsx("div", { className: "pills", children: children });
}
export function KeyValues({ items }) {
    return (_jsx("dl", { className: "key-values", children: items.map((item) => _jsxs("div", { children: [_jsx("dt", { children: item.label }), _jsx("dd", { children: item.value })] }, item.key)) }));
}
/** A tally: words on the left, a number on the right. */
export function Counts({ items }) {
    return (_jsx("dl", { className: "counts", children: items.map((item, i) => _jsxs("div", { children: [_jsx("dt", { children: item.label }), _jsx("dd", { children: item.count })] }, i)) }));
}
/** A divided list, for `RowItem`s or `PersonRow`s. */
export function Rows({ items, keyOf, render }) {
    return _jsx("ul", { className: "rows", children: items.map((item) => _jsx("li", { children: render(item) }, keyOf(item))) });
}
/**
 * One line of a list: something at the start, a title and a line below it, something at the end. `as` makes the whole
 * line a link: `<RowItem as={Link} to="/people/1" … />`.
 */
export function RowItem({ as, leading, title, note, sub, middle, end, ...rest }) {
    const Tag = as ?? 'div';
    return (_jsxs(Tag, { className: "row-item", ...rest, children: [leading, _jsxs("span", { className: "row-main", children: [_jsx("span", { className: "row-title", children: title }), note, sub && _jsx("span", { className: "row-sub", children: sub })] }), middle, end && _jsx("span", { className: "row-end", children: end })] }));
}
export function StatTile({ label, value, context, attention }) {
    return (_jsxs("div", { className: classes('tile', attention && 'attention'), children: [_jsx("div", { className: "tile-label", children: label }), _jsx("div", { className: "tile-value", children: value }), context && _jsx("div", { className: "tile-context", children: context })] }));
}
export function EmptyState({ title, children }) {
    return _jsxs("div", { className: "empty-state", children: [_jsx("h2", { children: title }), children] });
}
export function Loading({ children }) {
    return _jsx("p", { className: "loading", role: "status", children: children });
}
/** Says who can see a section, or that looking is recorded (DESIGN.md: privacy is visible in the UI). */
export function PrivacyNote({ children }) {
    return _jsxs("span", { className: "lock-note", children: [_jsx(Icon, { name: "lock" }), children] });
}
//# sourceMappingURL=components.js.map